// Modal that surfaces a PL/pgSQL function compile error and offers an
// AI-assisted fix. The user can review the suggestion, edit it inline,
// and re-deploy.

import { useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import {
  livePreviewFixCode, livePreviewRedeployFunction,
} from '../api'
import type { FunctionDeployResult } from '../types'
import { parseApiError } from '../utils'

interface Props {
  open: boolean
  project: string
  objName: string
  failure: FunctionDeployResult | null
  onClose: () => void
  onRedeployed: (result: FunctionDeployResult) => void
}

export default function FunctionErrorModal({
  open, project, objName, failure, onClose, onRedeployed,
}: Props) {
  const [aiLoading, setAiLoading] = useState(false)
  const [redeploying, setRedeploying] = useState(false)
  const [suggestion, setSuggestion] = useState<string>('')

  if (!failure) return null

  const askAi = async () => {
    setAiLoading(true)
    try {
      const res = await livePreviewFixCode(
        project, objName, failure.name,
        failure.source, failure.error_message || '',
      )
      if (res.suggestion) {
        setSuggestion(res.suggestion)
      } else {
        toast(res.message || 'No code suggestion returned')
      }
    } catch (err) {
      const { message } = parseApiError(err)
      toast.error(`AI fix failed: ${message}`)
    } finally {
      setAiLoading(false)
    }
  }

  const redeploy = async () => {
    const body = suggestion || failure.source
    setRedeploying(true)
    try {
      const res = await livePreviewRedeployFunction(
        project, objName, failure.name, body,
      )
      onRedeployed(res.result)
      if (res.result.status === 'ok') {
        toast.success(`Function ${failure.name} re-deployed`)
        onClose()
      } else {
        toast.error(`Still failing: ${res.result.error_message}`)
      }
    } catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Redeploy failed: ${message}`)
    } finally {
      setRedeploying(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} size="full"
           title={`Compile error in ${failure.name}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          color: '#ef4444',
          padding: 10, borderRadius: 4,
          fontFamily: 'var(--font-mono, monospace)', fontSize: '0.8425rem',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {failure.error_message}
          {failure.error_position != null && (
            <div style={{ marginTop: 6, fontSize: '0.8125rem', opacity: 0.8 }}>
              at character {failure.error_position}
            </div>
          )}
          {failure.error_hint && (
            <div style={{ marginTop: 6, fontSize: '0.8125rem', fontStyle: 'italic' }}>
              HINT: {failure.error_hint}
            </div>
          )}
        </div>

        <div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 6,
          }}>
            <strong style={{ fontSize: '0.8425rem' }}>Function source</strong>
            <button
              className="btn btn-ghost btn-sm"
              onClick={askAi}
              disabled={aiLoading || redeploying}
            >
              {aiLoading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={13} />}
              {aiLoading ? 'Thinking…' : 'Fix with AI'}
            </button>
          </div>
          <textarea
            value={suggestion || failure.source}
            onChange={e => setSuggestion(e.target.value)}
            spellCheck={false}
            style={{
              width: '100%', minHeight: 320,
              padding: 12, borderRadius: 4,
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg)',
              color: 'var(--color-text)',
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: '0.8425rem',
              outline: 'none', resize: 'vertical',
            }}
          />
        </div>
      </div>

      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose} disabled={redeploying}>
          Close
        </button>
        <button
          className="btn btn-primary"
          onClick={redeploy}
          disabled={redeploying || aiLoading}
        >
          {redeploying && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
          {redeploying ? 'Redeploying…' :
           suggestion ? 'Accept & Redeploy' : 'Redeploy as-is'}
        </button>
      </div>
    </Modal>
  )
}
